import React from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { ArrowLeft, LogOut, Plus, Trash2, UserX, UserCheck, Users } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { useApp } from '../context/AppContext';
import { useToast } from '../hooks/use-toast';

export default function AccountManager() {
  const nav = useNavigate();
  const { user, loading, logout, listUsers, createUser, setUserActive, deleteUser } = useApp();
  const { toast } = useToast();
  const [users, setUsers] = React.useState([]);
  const [busy, setBusy] = React.useState(false);
  const [form, setForm] = React.useState({ name: '', email: '', password: '' });

  React.useEffect(() => {
    if (!loading && !user) nav('/admin/login');
    else if (!loading && user && !user.isSuperAdmin) nav('/admin');
  }, [user, loading, nav]);

  const reload = React.useCallback(async () => {
    try {
      const items = await listUsers();
      setUsers(items);
    } catch (e) {
      toast({ title: 'Ошибка', description: e.message || 'Не удалось загрузить список', variant: 'destructive' });
    }
  }, [listUsers, toast]);

  React.useEffect(() => {
    if (user?.isSuperAdmin) reload();
  }, [user, reload]);

  if (!user?.isSuperAdmin) return null;

  const onCreate = async (e) => {
    e.preventDefault();
    setBusy(true);
    const payload = { ...form };
    try {
      await createUser(payload);
      setForm({ name: '', email: '', password: '' });
      toast({ title: 'Аккаунт создан', description: payload.email });
      await reload();
    } catch (err) {
      toast({ title: 'Ошибка', description: err.message, variant: 'destructive' });
    } finally {
      setBusy(false);
    }
  };

  const onToggleActive = async (u) => {
    const next = !u.isActive;
    const label = next ? 'активировать' : 'деактивировать';
    if (!window.confirm(`${label[0].toUpperCase() + label.slice(1)} «${u.email}»?`)) return;
    setBusy(true);
    try {
      await setUserActive(u.id, next);
      toast({ title: next ? 'Активирован' : 'Деактивирован', description: u.email });
      await reload();
    } catch (err) {
      toast({ title: 'Ошибка', description: err.message, variant: 'destructive' });
    } finally {
      setBusy(false);
    }
  };

  const onDelete = async (u) => {
    if (!window.confirm(`Удалить аккаунт «${u.email}» безвозвратно?`)) return;
    setBusy(true);
    try {
      await deleteUser(u.id);
      toast({ title: 'Удалён', description: u.email });
      await reload();
    } catch (err) {
      toast({ title: 'Ошибка', description: err.message, variant: 'destructive' });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-neutral-50">
      <div className="border-b border-neutral-200 bg-white">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-4 py-4 sm:px-6">
          <div className="flex items-center gap-3">
            <img src="/magnum-logo.png" alt="MAGNUM" className="h-11 w-11 rounded-md object-contain" />
            <div>
              <div className="text-base font-extrabold leading-tight sm:text-lg">MAGNUM <span className="text-red-600">ведущий дом спортивного покера</span></div>
              <div className="-mt-0.5 text-[10px] font-semibold uppercase tracking-[0.2em] text-neutral-500">Менеджер аккаунтов</div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <div className="hidden text-right text-sm sm:block">
              <div className="font-semibold text-neutral-900">{user.name}</div>
              <div className="text-xs text-neutral-500">{user.email}</div>
            </div>
            <Button variant="outline" onClick={() => { logout(); nav('/'); }}><LogOut className="mr-2 h-4 w-4" /> Выйти</Button>
          </div>
        </div>
      </div>

      <div className="mx-auto max-w-3xl px-4 py-8 sm:px-6">
        <Button variant="ghost" className="mb-4 -ml-2 text-neutral-600" onClick={() => nav('/admin')}>
          <ArrowLeft className="mr-2 h-4 w-4" /> К турнирам
        </Button>

        <div className="flex items-center gap-2">
          <Users className="h-6 w-6 text-red-600" />
          <h1 className="text-2xl font-bold text-neutral-900 sm:text-3xl">Аккаунты</h1>
        </div>
        <p className="mt-1 text-sm text-neutral-600">
          Создавайте админов клуба. Деактивация и удаление сразу закрывают доступ к кабинету.
        </p>

        <Card className="mt-6 rounded-2xl border-neutral-200">
          <CardContent className="p-6">
            <div className="text-xs font-semibold uppercase tracking-widest text-neutral-500">Новый аккаунт</div>
            <form onSubmit={onCreate} className="mt-4 grid gap-3 sm:grid-cols-2">
              <Input
                placeholder="Имя"
                value={form.name}
                onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                required
              />
              <Input
                type="email"
                placeholder="Email"
                value={form.email}
                onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                required
              />
              <Input
                type="password"
                placeholder="Пароль (мин. 6 символов)"
                value={form.password}
                onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))}
                minLength={6}
                required
                className="sm:col-span-2"
              />
              <Button type="submit" disabled={busy} className="bg-red-600 text-white hover:bg-red-700 sm:col-span-2">
                <Plus className="mr-2 h-4 w-4" /> Создать
              </Button>
            </form>
          </CardContent>
        </Card>

        <div className="mt-6 space-y-3">
          {users.map((u) => {
            const locked = u.isSuperAdmin || u.id === user.id;
            return (
              <Card key={u.id} className="rounded-2xl border-neutral-200">
                <CardContent className="flex flex-wrap items-center justify-between gap-3 p-4">
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <div className="truncate font-semibold text-neutral-900">{u.name || '—'}</div>
                      {u.isSuperAdmin && (
                        <Badge className="bg-amber-100 text-amber-800 hover:bg-amber-100">Главный</Badge>
                      )}
                      <Badge
                        className={u.isActive
                          ? 'bg-emerald-100 text-emerald-700 hover:bg-emerald-100'
                          : 'bg-neutral-100 text-neutral-600 hover:bg-neutral-100'}
                      >
                        {u.isActive ? 'Активен' : 'Деактивирован'}
                      </Badge>
                    </div>
                    <div className="truncate text-sm text-neutral-500">{u.email}</div>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      disabled={busy || locked}
                      onClick={() => onToggleActive(u)}
                      title={locked ? 'Недоступно для главного / себя' : undefined}
                    >
                      {u.isActive
                        ? <><UserX className="mr-1 h-4 w-4" /> Деактивировать</>
                        : <><UserCheck className="mr-1 h-4 w-4" /> Активировать</>}
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      className="text-neutral-500 hover:bg-red-50 hover:text-red-600"
                      disabled={busy || locked}
                      onClick={() => onDelete(u)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            );
          })}
          {users.length === 0 && (
            <div className="rounded-2xl border border-dashed border-neutral-300 bg-white py-10 text-center text-sm text-neutral-500">
              Пока нет аккаунтов
            </div>
          )}
        </div>

        <div className="mt-6 text-center text-xs text-neutral-400">
          <Link to="/admin" className="underline hover:text-neutral-600">Вернуться к турнирам</Link>
        </div>
      </div>
    </div>
  );
}
